import { ChangeEvent, FC } from 'react'
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { ICreateUser } from '../../../../models/User'
import { FieldWithErrorMessage } from '../../ui/FieldWithErrorMsg'

const PHONE_PREFIX = '+569'
const PHONE_PREFIX_DIGITS = '569'
const PHONE_TOTAL_LENGTH = 12
const PHONE_SUBSCRIBER_LENGTH = PHONE_TOTAL_LENGTH - PHONE_PREFIX.length

const normalizePhoneNumber = (value = '') => {
  const trimmedValue = String(value).trim()
  const digitsOnly = trimmedValue.replace(/\D/g, '')
  const subscriberDigits = digitsOnly.startsWith(PHONE_PREFIX_DIGITS)
    ? digitsOnly.slice(PHONE_PREFIX_DIGITS.length)
    : digitsOnly

  return `${PHONE_PREFIX}${subscriberDigits.slice(0, PHONE_SUBSCRIBER_LENGTH)}`
}

export interface Step1Values extends Omit<ICreateUser, 'token'> {
  password: string
}

const getStep1ValidationSchema = (requiresPassword: boolean) =>
  Yup.object<Step1Values>({
    firstname: Yup.string().required('Ingresa tu nombre'),
    lastname: Yup.string().required('Ingresa tu apellido'),
    phoneNumber: Yup.string()
      .required('Ingresa tu teléfono')
      .length(PHONE_TOTAL_LENGTH, 'Número de teléfono inválido')
      .test('startsWith+569', 'Debe comenzar con +569', value =>
        (value ?? '').startsWith(PHONE_PREFIX)
      ),
    email: Yup.string().email('Ingresa un correo válido').required('Ingresa tu correo'),
    password: requiresPassword
      ? Yup.string()
          .required('Ingresa tu contraseña')
          .test('minLength', 'Debe tener al menos 8 caracteres', value => value.length >= 8)
          .test('oneUppercase', 'Debe incluir al menos una letra mayúscula', value =>
            /[A-Z]/.test(value)
          )
          .test('oneLowercase', 'Debe incluir al menos una letra minúscula', value =>
            /[a-z]/.test(value)
          )
      : Yup.string(),
  })

interface Props {
  title: string
  subtitle?: string
  isLoading: boolean
  initialValues: Step1Values
  onSubmit: (values: Step1Values, actions: FormikHelpers<Step1Values>) => void
  isInvitation?: boolean
  isExistingFirebaseUser?: boolean
}

export const BaseFormCreateUser: FC<Props> = ({
  isLoading,
  initialValues,
  onSubmit,
  title,
  subtitle,
  isInvitation = false,
  isExistingFirebaseUser = false,
}) => {
  const normalizedInitialValues: Step1Values = {
    ...initialValues,
    phoneNumber: normalizePhoneNumber(initialValues.phoneNumber),
  }

  return (
    <Formik
      enableReinitialize
      validationSchema={getStep1ValidationSchema(!isExistingFirebaseUser)}
      initialValues={normalizedInitialValues}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values }) => (
        <Form className='base-create-user-form w-full bg-transparent px-1 sm:px-2 text-white space-y-5 sm:space-y-6'>
          {(title || subtitle) && (
            <div className='px-1 text-center space-y-2 sm:space-y-3'>
              {title && (
                <h3 className='font-sans text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-white lg:text-gray-800'>
                  {title}
                </h3>
              )}

              {subtitle && (
                <p className='mx-auto max-w-md font-sans text-sm leading-relaxed text-center text-gray-500'>
                  {subtitle}
                </p>
              )}
            </div>
          )}

          <div className='base-create-user-fields grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-5 md:grid-cols-2 md:gap-x-6 md:gap-y-6 lg:gap-x-8'>
            <div>
              <FieldWithErrorMessage
                name='firstname'
                type='text'
                placeholder='Ingresa tu nombre'
                label=''
              />
            </div>

            <div>
              <FieldWithErrorMessage
                name='lastname'
                type='text'
                placeholder='Ingresa tu apellido'
                label=''
              />
            </div>

            <div className='space-y-1.5'>
              <Field name='phoneNumber'>
                {({ field, form }: any) => {
                  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
                    form.setFieldValue('phoneNumber', normalizePhoneNumber(e.target.value))
                  }

                  const hasError = form.touched.phoneNumber && form.errors.phoneNumber

                  return (
                    <>
                      <div className='relative text-gray-600'>
                        <input
                          {...field}
                          id='phoneNumber'
                          type='text'
                          inputMode='numeric'
                          maxLength={PHONE_TOTAL_LENGTH}
                          minLength={PHONE_TOTAL_LENGTH}
                          value={normalizePhoneNumber(field.value)}
                          placeholder='Ingresa tu teléfono'
                          className={`
                            font-sans rounded-full
                            text-black placeholder:text-black/50
                            bg-white
                            ${hasError ? 'input-error' : 'input-primary'}
                          `}
                          onChange={handleChange}
                        />
                      </div>

                      {hasError && (
                        <div className='pl-4 pt-1'>
                          <ErrorMessage name='phoneNumber'>
                            {msg => <p className='text-sm text-orange-400'>{msg}</p>}
                          </ErrorMessage>
                        </div>
                      )}
                    </>
                  )
                }}
              </Field>
            </div>

            <div>
              <FieldWithErrorMessage
                disabled={isInvitation || isExistingFirebaseUser}
                name='email'
                type='email'
                placeholder='Ingresa tu correo'
                label=''
              />
            </div>

            {!isExistingFirebaseUser && (
              <div className='md:col-span-2'>
                <FieldWithErrorMessage
                  name='password'
                  type='password'
                  placeholder='Ingresa una contraseña segura'
                  label=''
                />
              </div>
            )}
          </div>

          <div className='base-create-user-actions flex w-full flex-col items-center gap-2 pt-1 sm:pt-2 md:w-1/2 mx-auto'>
            <div className='flex w-full justify-center'>
              <button
                disabled={isSubmitting}
                type='submit'
                className='base-create-user-submit w-full rounded-xl bg-primary py-4 text-sm font-semibold text-white shadow-md'
              >
                {isLoading
                  ? <span>Creando...</span>
                  : <span>{isExistingFirebaseUser ? 'Continuar registro' : '👉 Siguiente paso'}</span>}
              </button>
            </div>
          </div>

          {!isInvitation && (
            <p className='font-sans text-sm text-white/70 lg:text-gray-500 text-center leading-relaxed'>
              ¿Ya creaste tu cuenta o dejaste el registro pendiente?{' '}
              <Link
                to='/auth/signin'
                state={{ email: values.email }}
                className='text-accent1'
              >
                Inicia sesión para continuar
              </Link>
            </p>
          )}
        </Form>
      )}
    </Formik>
  )
}
